import React from "react";
import { motion } from "framer-motion";
import { 
  Target, 
  Users, 
  Lightbulb, 
  TrendingUp, 
  Award, 
  Zap 
} from "lucide-react";

// Mission Data - Replace with your BlogData
const MissionData = [
  {
    id: 1,
    icon: Target,
    name: "Client-Centric Approach",
    description: "We prioritize understanding your unique challenges and goals, delivering tailored solutions that drive measurable results.",
    color: "#264653"
  },
  {
    id: 2,
    icon: Lightbulb,
    name: "Innovation & Creativity",
    description: "We bring fresh perspectives and cutting-edge strategies to help your restaurant stand out in a competitive market.",
    color: "#2a9d8f"
  },
  {
    id: 3,
    icon: Users,
    name: "Collaborative Partnership",
    description: "We work alongside you as true partners, fostering open communication and shared success throughout our journey.",
    color: "#e76f51"
  },
  {
    id: 4,
    icon: TrendingUp,
    name: "Data-Driven Results",
    description: "Every recommendation is backed by solid analytics and industry insights to maximize your profitability and growth.",
    color: "#f4a261"
  },
  {
    id: 5,
    icon: Award,
    name: "Proven Expertise",
    description: "Our team brings 4+ years of hands-on experience in restaurant operations, ensuring practical and effective solutions.",
    color: "#e9c46a"
  },
  {
    id: 6,
    icon: Zap,
    name: "Rapid Implementation",
    description: "We focus on actionable strategies that can be implemented quickly to start seeing results without delay.",
    color: "#1b7840"
  }
];

const MissionSection = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section className="relative py-24 md:py-32 px-6 bg-[#F6F4EF] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-1/4 left-10 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-1/4 right-10 w-96 h-96 rounded-full bg-orange-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20 md:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Our Ideology
            </span>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] mb-6"
            style={{ letterSpacing: '-0.02em' }}
          >
            Our{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic font-serif" style={{ color: "#1b7840" }}>Mission</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            The principles and approaches that make us your ideal consulting partner
          </motion.p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {MissionData.map((item, index) => {
            const Icon = item.icon;
            
            return (
              <motion.div
                key={item.id}
                variants={cardVariants}
                whileHover={{ 
                  y: -12,
                  transition: { duration: 0.3 }
                }}
                className="group relative bg-white rounded-3xl p-8 md:p-10 shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}08 0%, transparent 100%)` 
                  }}
                />

                {/* Top accent line */}
                <motion.div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: item.color }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                />

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon Container */}
                  <motion.div
                    whileHover={{ 
                      scale: 1.1,
                      rotate: [0, -10, 10, 0],
                      transition: { duration: 0.5 }
                    }}
                    className="mb-6 inline-block"
                  >
                    <div 
                      className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center relative overflow-hidden"
                      style={{ backgroundColor: `${item.color}15` }}
                    >
                      {/* Shine effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                        initial={{ x: '-100%' }}
                        animate={{ x: '200%' }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          repeatDelay: 3,
                          ease: "linear"
                        }}
                      />
                      <Icon 
                        className="w-8 h-8 md:w-10 md:h-10 relative z-10"
                        style={{ color: item.color }}
                        strokeWidth={1.5}
                      />
                    </div>

                    {/* Glow effect on hover */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl blur-xl -z-10"
                      style={{ backgroundColor: item.color }}
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 0.2 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.div>

                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-primary transition-colors duration-300"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {item.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-base leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Bottom corner decoration */}
                <motion.div
                  className="absolute bottom-0 right-0 w-32 h-32 rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: `${item.color}05` }}
                />

                {/* Animated border on hover */}
                <motion.div
                  className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-primary/20 transition-colors duration-500"
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-20 md:mt-24"
        >
          <motion.div
            className="inline-block bg-white px-10 py-6 rounded-3xl shadow-lg"
            whileHover={{ scale: 1.02, y: -4 }}
          >
            <p className="text-gray-900 text-lg md:text-xl font-semibold mb-2">
              Ready to transform your restaurant?
            </p>
            <p className="text-gray-600 text-base">
              Let's discuss how we can help you achieve your goals
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionSection;